import * as yup from 'yup'

type AnyAddress = Partial<Address> | Partial<BillingAddress> | null | undefined

export type AddressFormValues = {
  firstName: string
  lastName: string
  companyName: string
  streetAddress: string
  houseNumber: string
  addressSuffix: string
  postalCode: string
  addressLocality: string
  addressCountry: string
}

export type BillingAddressFormValues = AddressFormValues & { vatId: string }

const emptyDelivery = (): AddressFormValues => ({
  firstName: '',
  lastName: '',
  companyName: '',
  streetAddress: '',
  houseNumber: '',
  addressSuffix: '',
  postalCode: '',
  addressLocality: '',
  addressCountry: ''
})

const emptyBilling = (): BillingAddressFormValues => ({
  ...emptyDelivery(),
  vatId: ''
})

const toFormBase = (address: AnyAddress): AddressFormValues | null => {
  if (!address) return null
  return {
    firstName: address.firstName ?? '',
    lastName: address.lastName ?? '',
    companyName: address.companyName ?? '',
    streetAddress: address.streetAddress ?? '',
    houseNumber: address.houseNumber ?? '',
    addressSuffix: address.addressSuffix ?? '',
    postalCode: address.postalCode ?? '',
    addressLocality: address.addressLocality ?? '',
    addressCountry:
      typeof address.addressCountry === 'object' && address.addressCountry !== null
        ? (address.addressCountry as { id: string }).id
        : (address.addressCountry as string | null | undefined) ?? ''
  }
}

export const transformAddressForForm = (address: AnyAddress): BillingAddressFormValues | null => {
  const base = toFormBase(address)
  if (!base) return null
  return { ...base, vatId: (address as Partial<BillingAddress>).vatId ?? '' }
}

export const createBillingAddressInitialValues = (
  address: AnyAddress
): BillingAddressFormValues => {
  return transformAddressForForm(address) ?? emptyBilling()
}

export const createDeliveryAddressInitialValues = (address: AnyAddress): AddressFormValues => {
  return toFormBase(address) ?? emptyDelivery()
}

export const billingAddressSchemaFields = {
  firstName: yup.string().nullable(),
  lastName: yup.string().nullable(),
  companyName: yup.string().nullable(),
  vatId: yup.string().nullable(),
  streetAddress: yup.string().required('Street is required'),
  houseNumber: yup.string().required('House number is required'),
  addressSuffix: yup.string().nullable(),
  postalCode: yup.string().required('ZIP code is required'),
  addressLocality: yup.string().required('City is required'),
  addressCountry: yup.string().required('Country is required')
}

export const deliveryAddressSchemaFields = {
  firstName: yup.string().nullable(),
  lastName: yup.string().nullable(),
  companyName: yup.string().nullable(),
  streetAddress: yup.string().required('Street is required'),
  houseNumber: yup.string().required('House number is required'),
  addressSuffix: yup.string().nullable(),
  postalCode: yup.string().required('ZIP code is required'),
  addressLocality: yup.string().required('City is required'),
  addressCountry: yup.string().required('Country is required')
}

export const billingAddressSchema = yup.object(billingAddressSchemaFields).required()
export const deliveryAddressSchema = yup.object(deliveryAddressSchemaFields).required()

export type BillingAddressFormSchema = yup.InferType<typeof billingAddressSchema>
export type DeliveryAddressFormSchema = yup.InferType<typeof deliveryAddressSchema>

export const sanitizeAddress = (address: any): any => {
  if (!address || typeof address !== 'object') return null
  const meaningfulFields = [
    'streetAddress',
    'houseNumber',
    'postalCode',
    'addressLocality',
    'addressCountry'
  ]
  const hasMeaningfulData = meaningfulFields.some(
    (f) => address[f] && String(address[f]).trim().length > 0
  )
  if (!hasMeaningfulData) return null

  const result: Record<string, any> = {}
  for (const key in address) {
    if (Object.prototype.hasOwnProperty.call(address, key)) {
      const value = address[key]
      result[key] = typeof value === 'string' && value.trim().length === 0 ? null : value
    }
  }
  return result
}

/**
 * `BillingAddressRequirementsValidator` (used on every Checkoutable.$billingAddress)
 * checks `array_key_exists` on every required field, so a payload missing any of
 * these keys gets a `missing_<field>` violation even when the underlying value is
 * null. The same check applies to `$deliveryAddress` (it's a JSON column with the
 * same shape). When we PUT a partial form to /carts or /orders, we therefore need
 * to emit ALL the canonical keys, falling back to the existing snapshot value (or
 * null) for fields the form does not collect.
 *
 * Keep this list in sync with `BillingAddressRequirementsValidator::$neededFields`
 * (api/src/Validator/Constraints/BillingAddressRequirementsValidator.php).
 */
const ADDRESS_REQUIRED_KEYS = [
  'addressCountry',
  'addressLocality',
  'addressRegion',
  'addressSuffix',
  'companyName',
  'firstName',
  'houseNumber',
  'lastName',
  'postOfficeBoxNumber',
  'postalCode',
  'streetAddress',
  'vatId'
] as const

export type AddressApiPayload = Record<(typeof ADDRESS_REQUIRED_KEYS)[number], string | null>

/**
 * Compose the JSON snapshot the API expects from form values + the existing
 * address on the cart/order. Returns a payload with every required key present
 * (null for any not collected by the form or carried over from the snapshot).
 */
export const buildAddressPayload = (
  formValues: Record<string, any>,
  existing: AnyAddress
): AddressApiPayload => {
  const merged = sanitizeAddress({ ...formValues }) ?? {}
  const snapshot: Record<string, any> = existing && typeof existing === 'object' ? existing : {}
  const result = {} as AddressApiPayload
  for (const key of ADDRESS_REQUIRED_KEYS) {
    const fromForm = (merged as any)[key]
    if (fromForm !== undefined) {
      result[key] = fromForm
      continue
    }
    const fromSnapshot = snapshot[key]
    if (typeof fromSnapshot === 'string' && fromSnapshot.length > 0) {
      result[key] = fromSnapshot
    } else if (fromSnapshot === null || fromSnapshot === undefined || fromSnapshot === '') {
      result[key] = null
    } else {
      result[key] = fromSnapshot
    }
  }
  return result
}
